import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/ForestOwner';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const FOREST_OWNER_API_REDUCER_KEY = 'forest-owners';
export const FOREST_OWNER_PATH = 'forest-owners';

export const forestOwnerApi = createApi({
    reducerPath: FOREST_OWNER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['ForestOwner', 'ForestOwners'],
    endpoints: (builder) => ({
        getForestOwnerList: builder.query<PaginationResult<Entity.ForestOwner>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ FOREST_OWNER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['ForestOwners'],
        }),
        getForestOwner: builder.query<Entity.ForestOwner, Entity.PrimaryForestOwner>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = forestOwnerNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ FOREST_OWNER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['ForestOwner'],
        }),
        getForestOwnerMultiple: builder.query<Entity.ForestOwner, Entity.PrimaryForestOwner[]>({
            query: (ids) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/ids${Entity.PrimaryForestOwner.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['ForestOwners'],
        }),
        getForestOwnerPresentation: builder.query<PaginationResult<Entity.ForestOwner>, Entity.PresentationForestOwnerRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ FOREST_OWNER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['ForestOwners'],
        }),
        
        insertForestOwner: builder.mutation<Entity.ForestOwner, Entity.CreateForestOwner>({
            query: (toInsert) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    forestOwnerApi.util.upsertQueryData('getForestOwner', Entity.PrimaryForestOwner.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['ForestOwners'],
        }),
        insertMultipleForestOwner: builder.mutation<Entity.ForestOwner[], Entity.CreateForestOwner[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            forestOwnerApi.util.upsertQueryData('getForestOwner', Entity.PrimaryForestOwner.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['ForestOwners'],
        }),
        updateForestOwner: builder.mutation<Entity.ForestOwner, Entity.UpdateEntityForestOwner>({
            query: (toUpdate) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['ForestOwners'],
        }),
        updateMultipleForestOwner: builder.mutation<Entity.ForestOwner[], Entity.UpdateEntityForestOwner[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['ForestOwners'],
        }),
        deleteForestOwner: builder.mutation<Entity.ForestOwner, Entity.ForestOwner>({
            query: (toDelete) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/${Entity.PrimaryForestOwner.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ForestOwners'],
        }),
        deleteMultipleForestOwner: builder.mutation<Entity.ForestOwner[], Entity.PrimaryForestOwner[]>({
            query: (ids) => {
                return ({
                    url: `/${ FOREST_OWNER_PATH }/batch${Entity.PrimaryForestOwner.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['ForestOwners'],
        }),
    }),
})

export const [forestOwnerNormalizer, forestOwnerNormalizerMiddleware] = createNormalizationMiddleware(FOREST_OWNER_API_REDUCER_KEY, forestOwnerApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetForestOwnerListQuery,
    useGetForestOwnerMultipleQuery,
    useGetForestOwnerPresentationQuery,
    useGetForestOwnerQuery,
    useInsertForestOwnerMutation,
    useInsertMultipleForestOwnerMutation,
    useUpdateForestOwnerMutation,
    useUpdateMultipleForestOwnerMutation,
    useDeleteForestOwnerMutation,
    useDeleteMultipleForestOwnerMutation,
    
} = forestOwnerApi
